/**
 * CTH Public Landing — Phase 2 (Founder-approved hybrid).
 * Base: Option B (Human + Data) + Option A whitespace restraint + Option C data depth.
 * Rollback: /app/memory/rollback/Landing_pre_phase2.jsx (+ git history).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, TrendingUp, Layers, Languages, MonitorSmartphone, Workflow } from 'lucide-react';
import { Logo } from '@/components/shared/Logo';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import ChallengeNavigator from '@/components/landing2/ChallengeNavigator';
import EcosystemSection from '@/components/landing2/EcosystemSection';
import ReportShowcase from '@/components/landing2/ReportShowcase';
import AnalyticsSection from '@/components/landing2/AnalyticsSection';
import HowItWorks from '@/components/landing2/HowItWorks';
import PackagesSection from '@/components/landing2/PackagesSection';
import ContactSection from '@/components/landing/ContactSection';
import { IMG_TEAM } from '@/data/landing2Data';

const FACTS = [
  { key: 'factSolutions', icon: Layers, color: '#176A70' },
  { key: 'factLang', icon: Languages, color: '#0D9488' },
  { key: 'factPortals', icon: MonitorSmartphone, color: '#D97706' },
  { key: 'factLifecycle', icon: Workflow, color: '#7C3AED' },
];

export default function Landing() {
  const { t, i18n } = useTranslation();
  const L = (k) => t(`pages.landing2.${k}`);
  const esLang = (i18n.language || 'es').startsWith('es');
  const slogan = (i18n.language || 'es').startsWith('es') ? 'Conecta, desarrolla, trasciende' : 'Connect. Develop. Transcend.';

  return (
    <div className="min-h-screen bg-white text-[#1F2430] antialiased" data-testid="landing-page">
      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="shrink-0"><Logo size="sm" /></div>
          <nav className="hidden lg:flex items-center gap-4 xl:gap-7 text-[13px] xl:text-sm font-medium text-slate-600 whitespace-nowrap">
            <a href="#desafios" className="hover:text-slate-900 transition-colors" data-testid="nav-challenges">{L('nav.challenges')}</a>
            <a href="#ecosistema" className="hover:text-slate-900 transition-colors" data-testid="nav-ecosystem">{L('nav.eco')}</a>
            <a href="#reportes" className="hover:text-slate-900 transition-colors" data-testid="nav-reports">{L('nav.reports')}</a>
            <a href="#resultados" className="hover:text-slate-900 transition-colors" data-testid="nav-results">{L('nav.results')}</a>
            <a href="#paquetes" className="hover:text-slate-900 transition-colors" data-testid="nav-packages">{L('nav.packages')}</a>
          </nav>
          <div className="flex items-center gap-2.5 shrink-0">
            <LanguageSwitcher compact />
            <Link to="/login" className="hidden sm:block text-sm font-medium text-slate-600 hover:text-slate-900 whitespace-nowrap" data-testid="landing-signin-btn">{L('nav.signIn')}</Link>
            <a href="#contact" className="text-xs sm:text-sm font-semibold text-white whitespace-nowrap rounded-full px-4 sm:px-5 py-2 sm:py-2.5 shadow-md hover:opacity-90 bg-[#176A70]" data-testid="landing-header-cta">
              <span className="hidden xl:inline">{L('nav.cta')}</span>
              <span className="xl:hidden">{esLang ? 'Agendar' : 'Book a call'}</span>
            </a>
          </div>
        </div>
      </header>

      {/* 1. HERO — Human + Data */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-24" data-testid="hero-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-100 px-4 py-1.5 text-xs font-semibold text-[#176A70]" data-testid="hero-badge">
              {L('hero.badge')}
            </span>
            <h1 className="mt-7 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="hero-title">
              {L('hero.titleA')} <span className="text-[#176A70]">{L('hero.titleB')}</span>
            </h1>
            <p className="mt-3 text-sm font-semibold tracking-wide text-[#30B0B0]" data-testid="hero-slogan">{slogan}</p>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="hero-sub">{L('hero.sub')}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#contact" className="inline-flex items-center gap-2 text-white rounded-full px-7 py-3.5 text-sm font-semibold shadow-lg hover:opacity-90 bg-[#176A70]" data-testid="hero-cta-primary">
                {L('hero.ctaPrimary')} <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#ecosistema" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold hover:border-slate-500" data-testid="hero-cta-secondary">
                {L('hero.ctaSecondary')}
              </a>
            </div>
            {/* Factual product signals — no invented performance metrics */}
            <div className="mt-12 grid grid-cols-2 gap-3" data-testid="hero-facts">
              {FACTS.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.key} className="flex items-center gap-3 rounded-2xl bg-white border border-slate-100 shadow-sm px-4 py-3">
                    <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: `${f.color}12` }}>
                      <Icon className="h-4 w-4" style={{ color: f.color }} />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">{L(`hero.${f.key}`)}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative" data-testid="hero-visual">
            <img src={IMG_TEAM} alt="" className="rounded-[2rem] shadow-2xl w-full object-cover aspect-[4/3]" />
            <span className="absolute top-4 left-4 text-[9px] uppercase tracking-wider text-white/90 bg-black/35 rounded-full px-2.5 py-1">{L('hero.illustrative')}</span>
            <div className="absolute -bottom-6 -left-4 sm:-left-6 rounded-2xl bg-white shadow-xl border border-slate-100 p-4 w-52">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500"><TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> {L('hero.chipClimate')}</div>
              <div className="text-2xl font-extrabold mt-1">79<span className="text-sm text-emerald-600 ml-2">▲ +8</span></div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500" style={{ width: '79%' }} /></div>
            </div>
            <div className="absolute -top-5 -right-2 sm:-right-4 rounded-2xl bg-white shadow-xl border border-slate-100 px-4 py-3">
              <div className="text-xs font-semibold text-slate-500">{L('hero.chipCompat')}</div>
              <div className="text-2xl font-extrabold text-[#176A70]">87%</div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CHALLENGE NAVIGATOR */}
      <ChallengeNavigator />

      {/* 3. ECOSYSTEM — 13 market-facing solutions in lifecycle groups */}
      <EcosystemSection />

      {/* 4. PSYCHOMETRIC / TALENTMAP REPORT */}
      <ReportShowcase />

      {/* 5. EXECUTIVE ANALYTICS */}
      <AnalyticsSection />

      {/* 6. HOW CTH WORKS */}
      <div className="bg-white border-y border-slate-100"><HowItWorks /></div>

      {/* 7. CUSTOM PACKAGES */}
      <PackagesSection />

      {/* 9. FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="final-cta-section">
        <div className="relative rounded-[2.5rem] overflow-hidden">
          <img src={IMG_TEAM} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#1B2834]/85" />
          <div className="relative px-8 py-16 lg:py-20 text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-extrabold max-w-2xl mx-auto" data-testid="final-cta-title">{L('cta.heading')}</h2>
            <p className="mt-4 text-white/75 max-w-xl mx-auto">{L('cta.sub')}</p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <a href="#contact" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0E4A4F] px-8 py-4 text-sm font-bold hover:opacity-90" data-testid="final-cta-primary">
                {L('cta.btn')} <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/login" className="rounded-full border border-white/40 px-8 py-4 text-sm font-semibold hover:bg-white/10" data-testid="final-cta-login">{L('cta.login')}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* V5.4 §11 — candidate entry point */}
      <section className="py-14 bg-white" data-testid="landing-candidates">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-800">
            {esLang ? '¿Buscas tu próxima oportunidad?' : 'Looking for your next opportunity?'}
          </h2>
          <p className="mt-3 text-slate-500 max-w-xl mx-auto text-sm">
            {esLang
              ? 'Postúlate a una vacante publicada por nuestras empresas cliente (con el enlace de la vacante) o entrega tu CV al banco de talento de Corporate Talent Hub.'
              : 'Apply to a vacancy published by our client companies (via the vacancy link) or submit your CV to the Corporate Talent Hub talent pool.'}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Link to="/talent-pool" className="inline-flex items-center gap-2 rounded-full bg-[#1B2834] text-white px-7 py-3.5 text-sm font-bold hover:opacity-90" data-testid="landing-talent-pool-btn">
              {esLang ? 'Entregar mi CV al Talent Pool' : 'Submit my CV to the Talent Pool'} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">
            {esLang
              ? 'Tu CV se entrega a Corporate Talent Hub (H&M Advisory) bajo consentimiento expreso; nunca se comparte automáticamente con empresas.'
              : 'Your CV is submitted to Corporate Talent Hub (H&M Advisory) under explicit consent; it is never shared automatically with companies.'}
          </p>
        </div>
      </section>

      {/* Contact / lead form — existing safe behavior (POST /api/leads) */}
      <ContactSection />

      <footer className="border-t border-slate-100 bg-white" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Logo size="sm" />
            <p className="mt-2 text-xs font-semibold tracking-wide text-[#30B0B0]" data-testid="footer-slogan">{slogan}</p>
          </div>
          <span className="text-sm text-slate-500">{L('footer.rights')}</span>
        </div>
      </footer>
    </div>
  );
}
